import {
  styled,
  Box,
  IconButton,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import colors from "../../theme/Colours";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
export const ProductDetailsContainer = styled(Box)({
  backgroundColor: colors.background2,
  width: "100%",
  marginBottom: "60px",
  height: "auto",
  overflow: "hidden",
  borderRadius: "4px",
});

export const ContentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "22px",
  padding: theme?.breakpoints.down("sm") ? "57px 29px 57px 16px" : "18px 8px",
  borderRadius: "15px",
  flexDirection: "column",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

export const ImageSection = styled(Box)({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "space-around",
  width: "100%",
  "@media (min-width: 900px)": {
    width: "50%",
  },
});
export const ProductDetailsHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  fontSize: "22px",
  marginBottom: theme?.spacing(2) || "16px",
  [theme.breakpoints.up("md")]: {
    fontSize: "36px",
  },
}));
export const MainImageContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 300,
  backgroundColor: colors.mediumGrey,
  borderRadius: "16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    height: 350,
  },
  [theme.breakpoints.up("md")]: {
    height: 444,
  },
}));

interface MainImageProps {
  isOutOfStock?: boolean;
}

export const MainImage = styled("img", {
  shouldForwardProp: (prop) => prop !== "isOutOfStock",
})<MainImageProps>(({ isOutOfStock }) => ({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  ...(isOutOfStock ? { filter: "grayscale(100%)" } : {}),
}));

export const ThumbnailsContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "15px",
  marginTop: "20px",
  padding: "10px 0px",
  overflow: "hidden",
});

export const ThumbnailButton = styled(IconButton)({
  "&:hover": { background: "transparent" },
});

export const ThumbnailsImagesContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "5px",
  transition: "transform 0.5s ease-in-out",
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    gap: "10px",
  },
}));

interface ThumbnailImageProps {
  isActive?: boolean;
  isOutOfStock?: boolean;
}

export const ThumbnailImage = styled("img", {
  shouldForwardProp: (prop) => prop !== "isActive" && prop !== "isOutOfStock",
})<ThumbnailImageProps>(({ isActive, isOutOfStock }) => ({
  width: 49,
  height: 89,
  padding: "9px 31.2px 9px 29.2px",
  borderRadius: "16px",
  objectFit: "cover",
  backgroundColor: colors.lightGrey,
  cursor: "pointer",
  border: "2px solid transparent",
  transition: "border 0.3s ease",
  ...(isActive ? { border: `1.5px solid ${colors.primaryButtonOrange}` } : {}),
  ...(isOutOfStock ? { filter: "grayscale(100%)" } : {}),
}));

export const ArrowIcon = styled("img")({
  width: "50px",
  height: "50px",
  padding: "4px !important",
  borderRadius: "50% !important",
});

export const RightArrowIcon = styled(ArrowIcon)`
  transform: scaleX(-1);
`;

export const DetailsSection = styled(Box)({
  width: "100%",
  paddingBottom: 4,
  position: "relative",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  "@media (min-width: 900px)": {
    width: "50%",
    paddingBottom: 10,
  },
});

export const EditContainer = styled("span")({
  display: "flex",
  justifyContent: "flex-end",
  marginRight: "16px",
  marginTop: "16px",
});

export const TitleEditContainer = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  marginBottom: "20px",
});

interface ProductTitleProps {
  isOutOfStock?: boolean;
}

export const ProductTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isOutOfStock",
})<ProductTitleProps>(({ isOutOfStock }) => ({
  fontSize: "36px",
  fontFamily: "Poppins",
  fontWeight: 600,
  lineHeight: "48px",
  marginLeft: "10px",
  color: isOutOfStock ? colors.redText : colors.black,
  flex: 1,
  "@media (min-width: 900px)": {
    fontSize: "30px",
  },
}));
export const AbvIbuInfo = styled(Typography)(({ theme }) => ({
  fontSize: "36px",
  fontWeight: 600,
  lineHeight: 1.5,
  color: colors.black,
  marginBottom: theme?.spacing(1) || "10px",
  [theme.breakpoints.up("md")]: {
    fontSize: "28px",
  },
}));

export const DescriptionContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  position: "relative",
  marginBottom: 3,
});

export const DescriptionText = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontFamily: "Roboto",
  fontWeight: 400,
  lineHeight: "28px",
  color: colors.mutedText,
  marginBottom: "10px",
  whiteSpace: "pre-wrap",
  [theme.breakpoints.up("sm")]: {
    fontSize: "16px",
  },
}));

export const ReadMoreContainer = styled(Box)({
  textAlign: "right",
  marginBottom: 2,
});

export const ReadMoreText = styled("span")({
  fontFamily: "Roboto",
  color: colors.mediumBlue,
  cursor: "pointer",
  fontWeight: 450,
  backgroundColor: "transparent",
  marginLeft: "auto",
  marginRight: "18px",
});

export const ActionsContainer = styled(Box)({
  marginTop: "auto",
  "@media (max-width: 899px)": {
    marginTop: 0,
  },
});

export const DropdownQuantityContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "column",
  gap: "16px",
  marginBottom: 4.5,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    gap: "40px",
  },
  "@media (min-width: 900px)": {
    width: 180,
  },
}));

export const TypeDropdownContainer = styled(Box)({
  flex: 1,
  minWidth: "150px",
  display: "flex",
  flexDirection: "column",
});

export const CustomDropdownButtonStyles = {
  backgroundColor: colors.background2,
  border: `1px solid ${colors.borderGrey}`,
  width: "100%",
  height: "48px",
};
export const TypeLabel = styled(Typography)({
  fontSize: "14px",
  fontWeight: "500",
  color: colors.mutedText,
  paddingBottom: "6px",
  width: "100%",
  textAlign: "left",
  marginLeft: 1,
});

export const QuantityContainer = styled(Box)({
  flex: 1,
  minWidth: "150px",
  display: "flex",
  flexDirection: "column",
  height: "75px",
});

export const QuantityControlBox = styled(Box)(({ theme }) => ({
  width: "80px",
  height: "28px",
  border: `1px solid ${colors.borderGrey}`,
  borderRadius: "5px",
  backgroundColor: colors.background2,
  display: "flex",
  alignItems: "center",
  [theme.breakpoints.up("sm")]: {
    height: "48px",
  },
}));

export const QuantityText = styled(Typography)(({ theme }) => ({
  fontFamily: "Roboto",
  fontWeight: 400,
  fontSize: "12px",
  flex: 1,
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    fontSize: "14px",
  },
}));

interface QuantityIconButtonProps {
  downButton?: boolean;
}

export const QuantityIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "downButton",
})<QuantityIconButtonProps>(({ downButton }) => ({
  padding: "2px",
  ...(downButton && {
    marginTop: "-12px",
  }),
  "& svg": {
    fontSize: "20px",
    "@media (min-width: 600px)": {
      fontSize: "20px",
    },
  },
}));

export const PriceContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: 2,
  width: "100%",
  gap: 2,
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    marginTop: 4,
    gap: 4,
    overflow: "hidden",
  },
}));

interface PriceTextProps {
  isOutOfStock?: boolean;
}

export const PriceText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isOutOfStock",
})<PriceTextProps>(({ isOutOfStock }) => ({
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  fontSize: "24px",
  color: isOutOfStock ? colors.redText : colors.black,
  marginTop: 15,
  "@media (min-width: 600px)": {
    fontSize: "28px",
  },
}));

export const PriceTextContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme?.spacing(2) || "16px",
  whiteSpace: "nowrap",
  minWidth: "30%",
  flexShrink: 1,
  [theme.breakpoints.up("sm")]: {
    alignItems: "flex-start",
    maxWidth: "60%",
  },
}));

export const PriceButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: "4px",
  marginTop: 1,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    gap: "8px",
    width: "auto",
    flexWrap: "nowrap",
  },
  "@media (min-width: 900px)": {
    gap: "14px",
  },
}));

export const ActionButton = styled(Button)(({ theme }) => ({
  fontSize: "11px",
  width: "100%",
  height: "45px",
  borderRadius: "6px",
  border: `1px solid ${colors.primaryButtonOrange}`,
  backgroundColor: colors.background2,
  color: colors.primaryButtonOrange,
  "&:hover": {
    backgroundColor: colors.primaryButtonOrange,
    color: colors.background2,
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "14px",
    minWidth: 120,
    width: "auto",
    padding: "0 8px",
    flexShrink: 1,
  },
}));

export const OutOfStockText = styled(Typography)({
  color: colors.redText,
  fontWeight: 600,
});

export const CustomDivider = styled(Divider)(({ theme }) => ({
  borderColor: `${colors.greytext} !important`,
  marginTop: "10px",
  marginBottom: "20px",
  [theme.breakpoints.up("md")]: {
    marginBottom: "30px",
    marginTop: "20px",
  },
}));

export const ActionsDivider = styled(Divider)(() => ({
  borderColor: `${colors.greytext} !important`,
  my: 4,
}));

export const BackArrowIcon = styled("img")({
  width: 40,
  cursor: "pointer",
});

export const EditIconStyled = styled("img")({
  width: 20,
  height: 20,
});

export const FavoriteIconStyled = styled(FavoriteIcon)(({ theme }) => ({
  color: colors.redText,
  fontSize: 20,
  [theme.breakpoints.up("md")]: {
    fontSize: 24,
  },
}));

export const FavoriteBorderIconStyled = styled(FavoriteBorderOutlinedIcon)(
  ({ theme }) => ({
    color: colors.black,
    fontSize: 20,
    [theme.breakpoints.up("md")]: {
      fontSize: 24,
    },
  })
);
